import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialModule } from '@core/modules';
import { AuthService, LoggerService } from '@core/services';
import { AuthenticatorAppsComponent } from './authenticator-apps.component';
import { startAuthentication } from '@simplewebauthn/browser';
import { Subscription, firstValueFrom } from 'rxjs';
import { UiIllustrationComponent } from '../../shared/ui';

/**
 * MFA challenge screen (task 5.1).
 *
 * Handles BOTH challenge purposes the backend issues:
 * - `mfa_verify` — step 2 of password login: enter the TOTP code from the
 *   authenticator, `AuthService.verifyMfa()` completes the session.
 * - `mfa_enroll` — mandatory enrollment after account setup (or admin MFA
 *   reset): first `AuthService.mfaEnroll()` returns the `otpauth://` URI to
 *   render as a QR code, then the same code entry + verify completes.
 *
 * CREDENTIAL HANDLING (non-negotiable): the challengeToken and the TOTP code
 * live in COMPONENT STATE ONLY (signals / plain fields). They are never
 * written to localStorage or sessionStorage, never put in the URL, and never
 * logged. The challenge token proves the PASSWORD step only — it is NOT
 * authentication; this screen grants nothing until `/mfa/verify` succeeds
 * and the backend sets the session cookie.
 *
 * The challenge token arrives via a one-shot in-memory handoff from the
 * login/setup screen (see `MfaChallengeBridge`). If this screen is loaded
 * WITHOUT a pending challenge (direct URL navigation, refresh after
 * completion), there is nothing to verify — it redirects to login rather
 * than rendering a dead form. This is the MFA-step-skipping defense: no
 * route, guard, or refresh can reach an authenticated state from here
 * without a backend-verified code.
 */
/** Owner 2026-10-22: how long "Valid – Logging in…" shows after a correct code. */
export const LOGIN_PAUSE_MS = 3000;

@Component({
  selector: 'app-mfa-challenge',
  standalone: true,
  imports: [MaterialModule, AuthenticatorAppsComponent, UiIllustrationComponent],
  templateUrl: './mfa-challenge.component.html',
  styleUrl: './mfa-challenge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaChallengeComponent implements OnDestroy, AfterViewInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private logger = inject(LoggerService);

  /** Challenge purpose from the bridge: 'verify' (login) or 'enroll' (setup). */
  readonly purpose = signal<'verify' | 'enroll'>('verify');

  /**
   * Which MFA method the screen is currently driving (Phase 2, task 2.5).
   * Orthogonal to `purpose` — seeded from the staged challenge's hasPasskey
   * flag ('passkey' when true, 'totp' otherwise) and switchable via the
   * fallback link. A TOTP-only contact never sees this toggle: hasPasskey is
   * false, mfaMethod stays 'totp', and the flow below is byte-for-byte the
   * pre-passkey behavior.
   */
  readonly mfaMethod = signal<'totp' | 'passkey'>('totp');

  /** Whether the staged challenge offers the passkey option at all. */
  readonly hasPasskey = signal<boolean>(false);

  /** Passkey ceremony in flight (options fetched / prompt open / verifying). */
  readonly passkeyBusy = signal<boolean>(false);

  /** Passkey failure message (never shown for user cancellation). */
  readonly passkeyError = signal<string>('');

  /** otpauth:// URI for QR rendering (enroll mode only). */
  readonly otpauthUri = signal<string | null>(null);

  /** Fixed 6-box layout — TOTP codes are always 6 digits (RFC 6238/ParentTotp.cfc). */
  readonly digitIndexes = [0, 1, 2, 3, 4, 5] as const;

  /** One digit per box — component state only, never persisted. */
  private readonly codeChars = signal<string[]>(['', '', '', '', '', '']);

  /** The 6-digit code as typed, assembled from the boxes. */
  readonly code = computed(() => this.codeChars().join(''));

  /** Each box's displayed value, read by the template. */
  readonly digits = computed(() => this.codeChars());

  private readonly digitBoxes = viewChildren<ElementRef<HTMLInputElement>>('digitBox');


  readonly isLoading = signal<boolean>(false);
  readonly errorMessage = signal<string>('');

  /**
   * R30: "mm:ss" only once the sign-in is nearly out of time (< 1 minute) —
   * a countdown beside authenticator codes that change every 30 s confuses
   * parents. Null while no expiry could be decoded or more than a minute is left.
   */
  readonly remainingLabel = computed(() => {
    const seconds = this.remainingSeconds();
    if (seconds === null || seconds >= 60) {
      return null;
    }
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  });

  /** Whether the enroll step still needs the QR (secret not yet issued). */
  readonly needsEnrollment = computed(() => this.purpose() === 'enroll' && !this.otpauthUri());

  /** Code is a 6-digit TOTP — enable submit only when plausibly complete. */
  readonly canSubmit = computed(
    () => /^\d{6}$/.test(this.code()) && !this.isLoading() && this.remainingSeconds() !== 0,
  );

  /** The challenge token — component state ONLY (never persisted, never logged). */
  private challengeToken: string | null = null;

  private authSub: Subscription | null = null;

  /**
   * Live countdown to the challenge token's expiry (reported live 2026-09-15:
   * a stale challenge — e.g. left on this screen past the 5-minute TTL — fails
   * with the SAME generic message as a wrong code, which reads as a mystery
   * bug). Decoded client-side from the token's own `exp` claim purely for
   * DISPLAY; the backend is still the sole source of truth (it re-verifies
   * the signature and expiry independently on every /mfa/verify call). This
   * is not a trust boundary — nothing here can grant access, only show a
   * clock and redirect early with an honest reason instead of letting a
   * doomed submit land on the generic "wrong code" message.
   */
  readonly remainingSeconds = signal<number | null>(null);
  private expiresAtEpoch: number | null = null;
  private countdownHandle: ReturnType<typeof setInterval> | null = null;

  constructor() {
    const pending = MfaChallengeBridge.consumePendingChallenge();
    if (!pending) {
      // Direct navigation / refresh without a pending challenge: nothing to
      // verify. Redirect to login — do NOT render a dead form.
      this.logger.warn('MFA challenge screen loaded without a pending challenge — redirecting to login');
      void this.router.navigate(['/login']);
      return;
    }

    this.challengeToken = pending.challengeToken;
    this.purpose.set(pending.purpose);
    // Phase 2 (task 2.5): seed the method choice from the login response.
    // A TOTP-only contact (hasPasskey false) stays on 'totp' — their flow is
    // byte-for-byte unchanged.
    this.hasPasskey.set(pending.hasPasskey);
    this.mfaMethod.set(pending.hasPasskey ? 'passkey' : 'totp');

    // An authenticated user has no business here.
    if (this.authService.isAuthenticated) {
      void this.router.navigate(['/dashboard']);
      return;
    }

    this.expiresAtEpoch = decodeChallengeExpiry(pending.challengeToken);
    if (this.expiresAtEpoch !== null) {
      this.startCountdown();
    }

    if (pending.purpose === 'enroll') {
      void this.beginEnrollment();
    }
  }

  private startCountdown(): void {
    const tick = (): void => {
      if (this.expiresAtEpoch === null) {
        return;
      }
      const remaining = Math.max(0, this.expiresAtEpoch - Math.floor(Date.now() / 1000));
      this.remainingSeconds.set(remaining);
      if (remaining <= 0) {
        this.handleExpiry();
      }
    };
    tick();
    this.countdownHandle = setInterval(tick, 1000);
  }

  /** Countdown hit zero: leave before a submit can land on the generic
   *  "wrong code" message for what is actually an expired session. */
  private handleExpiry(): void {
    if (this.countdownHandle !== null) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = null;
    }
    this.challengeToken = null;
    // Phase 2: the ceremony token is bound to the same expired handoff —
    // drop it so a late "Use passkey" click cannot submit a doomed verify.
    this.waChallengeToken = null;
    this.waOptionsJson = null;
    void this.router.navigate(['/login'], { queryParams: { reason: 'mfa_timeout' } });
  }

  /** Request the TOTP secret (enroll mode) — returns the otpauth URI for QR. */
  private async beginEnrollment(): Promise<void> {
    if (!this.challengeToken) {
      return;
    }
    this.isLoading.set(true);
    this.errorMessage.set('');
    try {
      const result = await firstValueFrom(this.authService.mfaEnroll(this.challengeToken));
      this.otpauthUri.set(result.otpauthUri);
      this.focusBox(0);
    } catch {
      // Generic failure — the challenge may be expired/used. Same message for
      // every failure shape (the backend is already non-distinguishing).
      this.errorMessage.set('Enrollment could not be started. Please log in again.');
      this.challengeToken = null;
    } finally {
      this.isLoading.set(false);
    }
  }

  ngAfterViewInit(): void {
    if (this.needsEnrollment()) {
      return; // QR still loading — nothing to focus yet.
    }
    this.focusBox(0);
  }

  /** A single box changed — keep only the last digit typed and advance focus. */
  onDigitInput(index: number, rawValue: string): void {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    const chars = [...this.codeChars()];
    chars[index] = digit;
    this.codeChars.set(chars);
    if (digit && index < 5) {
      this.focusBox(index + 1);
    }
    this.autoSubmit();
  }

  /** Verify as soon as all six digits are in (owner 2026-10-22); onSubmit's guards stop repeats. */
  private autoSubmit(): void {
    if (/^\d{6}$/.test(this.code())) void this.onSubmit();
  }

  /** Backspace/arrow navigation between boxes (native input handles digit deletion). */
  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.codeChars()[index] && index > 0) {
      event.preventDefault();
      const chars = [...this.codeChars()];
      chars[index - 1] = '';
      this.codeChars.set(chars);
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusBox(index - 1);
      return;
    }
    if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusBox(index + 1);
    }
  }

  /** Pasting a full code fills every box at once instead of just the first. */
  onPaste(event: ClipboardEvent): void {
    const digits = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (!digits) {
      return;
    }
    event.preventDefault();
    const chars = digits.split('');
    while (chars.length < 6) {
      chars.push('');
    }
    this.codeChars.set(chars);
    this.focusBox(Math.min(digits.length, 5));
    this.autoSubmit();
  }

  private focusBox(index: number): void {
    // The view updates on the next tick after a signal write — defer the
    // focus call so it lands on the box that's actually in the DOM.
    setTimeout(() => this.digitBoxes()[index]?.nativeElement.focus());
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit() || !this.challengeToken) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');
    const token = this.challengeToken;
    const code = this.code();

    try {
      // verifyMfa() confirms the fresh session via whoami and flips the app
      // to authenticated. The challenge token is single-use server-side;
      // drop it from memory immediately after the attempt either way.
      this.challengeToken = null;
      await firstValueFrom(this.authService.verifyMfa(token, code));
      // R30: no "Valid – Logging in…" banner — the button's single verifying
      // state is the whole story, so a correct code goes straight on.
      // Session is live. A passkey-capable contact with no passkey registered
      // sees the registration offer inside Security Settings (DCM-362: no
      // detour here — every successful code lands on completeLogin()).
      this.completeLogin();
    } catch (error: unknown) {
      // Wrong code, expired challenge, lockout — one generic message for all
      // shapes (anti-enumeration parity with the backend).
      this.logger.error('MFA verification failed', error);
      // R30: one plain sentence, and the boxes are cleared for the newest code.
      this.errorMessage.set("That code didn't work – try the newest code from your app");
      this.codeChars.set(['', '', '', '', '', '']);
      this.isLoading.set(false);
      // Allow a retry with a fresh code (the challenge may still be valid —
      // it is single-use only on SUCCESS; a failed verify does not consume it
      // server-side, but we cannot know which failure occurred, so re-issue
      // from the bridge snapshot is not possible — keep the token only when
      // the failure is plausibly a wrong code). Skip the restore if the
      // countdown says it's expired anyway — the tick that follows will
      // redirect to /login with an honest reason instead of inviting a
      // second doomed submit.
      if (this.expiresAtEpoch === null || Math.floor(Date.now() / 1000) < this.expiresAtEpoch) {
        this.challengeToken = token;
      }
    }
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
    if (this.countdownHandle !== null) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = null;
    }
    // Credential hygiene: drop the challenge token and code when leaving the
    // screen for any reason (component state only — nothing to persist).
    this.challengeToken = null;
    this.codeChars.set(['', '', '', '', '', '']);
    // Phase 2: drop the ceremony token + retained options too (same
    // non-persistence convention).
    this.waChallengeToken = null;
    this.waOptionsJson = null;
  }

  // ── Passkey branch (Phase 2, task 2.5) ───────────────────────────────────────

  /**
   * The passkey_assertion ceremony token — component state ONLY (never
   * persisted, never logged), same discipline as the step-1 challenge token.
   *
   * RETRY SEMANTICS (Claude review fix, BLOCKING 1): the backend consumes the
   * ceremony token on SUCCESS only (single-use-on-success, mirroring
   * /mfa/verify), so after a cancelled prompt or a failed assertion the token
   * is still server-side-valid within its 2-minute TTL. This component
   * therefore RETAINS it (and the assertion options) until a verify call
   * actually succeeds or the countdown expires — a retry re-runs the browser
   * prompt against the SAME token instead of silently no-oping.
   */
  private waChallengeToken: string | null = null;

  /**
   * The assertion options JSON from the last successful
   * passkeyAuthenticationOptions() call — retained alongside waChallengeToken
   * so a retry can re-run the browser prompt without re-minting (the step-1
   * token is already consumed server-side and cannot mint a second ceremony).
   */
  private waOptionsJson: string | null = null;

  /** Switch to the TOTP view (fallback link from the passkey view). */
  useTotpInstead(): void {
    this.mfaMethod.set('totp');
    this.passkeyError.set('');
    // The step-1 challenge token was consumed server-side the moment the
    // ceremony token was minted (passkeyAuthenticationOptions is single-use),
    // so onSubmit() can never succeed with it. The TOTP fallback therefore
    // RE-MINTS: it re-runs the login handoff by sending the parent back to
    // /login with an honest reason, rather than showing a form that can only
    // ever fail. (The ceremony token is retained — "Use passkey instead"
    // can still complete the login without a re-login.)
    if (!this.challengeToken) {
      this.challengeToken = null;
      void this.router.navigate(['/login'], { queryParams: { reason: 'mfa_timeout' } });
      return;
    }
    // challengeToken still present = no ceremony was ever attempted (the user
    // clicked the fallback before "Use passkey") — the original TOTP flow
    // works as-is.
    setTimeout(() => this.focusBox(0));
  }

  /** Switch back to the passkey view (from the TOTP fallback). */
  usePasskeyInstead(): void {
    if (!this.hasPasskey()) {
      return;
    }
    this.mfaMethod.set('passkey');
    this.passkeyError.set('');
  }

  /**
   * Run the passkey assertion ceremony:
   * passkeyAuthenticationOptions() -> browser startAuthentication() ->
   * passkeyAuthenticationVerify(). On success the backend sets the session
   * cookie and completeLogin() navigates — the same post-verify flow as
   * onSubmit().
   *
   * RETRY/FALLBACK CONTRACT (Claude review fix, BLOCKING 1):
   * - First run: mints options + ceremony token (consuming the step-1 token
   *   server-side), then runs the browser prompt.
   * - Cancelled prompt (NotAllowedError/AbortError): silent return to the
   *   method-choice state; the ceremony token + options are RETAINED, so the
   *   next "Use passkey" click re-runs the prompt directly (no re-mint).
   * - Failed assertion (bad signature, wrong device): the backend leaves the
   *   ceremony token unconsumed (single-use-on-success), so it is RETAINED
   *   for a retry; the error message offers the TOTP fallback honestly.
   * - TOTP fallback after a ceremony attempt: useTotpInstead() re-runs the
   *   login handoff (the step-1 token is gone; a code submit could never
   *   succeed) — see that method's comment.
   */
  async onUsePasskey(): Promise<void> {
    if (this.passkeyBusy()) {
      return;
    }
    this.passkeyBusy.set(true);
    this.passkeyError.set('');
    try {
      // 1. Mint the assertion options + ceremony token — ONLY on the first
      //    run. A retry reuses the retained pair (the step-1 token was
      //    consumed server-side when the options were minted and cannot mint
      //    a second ceremony).
      if (!this.waChallengeToken || !this.waOptionsJson) {
        if (!this.challengeToken) {
          // No step-1 token and no retained ceremony state: the handoff is
          // exhausted (e.g. the countdown expired mid-ceremony). Fail closed
          // with an honest message instead of a silent no-op.
          this.passkeyBusy.set(false);
          this.passkeyError.set('Your sign-in session expired. Please log in again.');
          return;
        }
        const step1Token = this.challengeToken;
        const options = await firstValueFrom(this.authService.passkeyAuthenticationOptions(step1Token));
        this.challengeToken = null;
        this.waChallengeToken = options.waChallengeToken;
        this.waOptionsJson = options.publicKeyCredentialRequestOptions;
      }

      // 2. Browser ceremony — the platform passkey prompt (re-runs on retry
      //    against the retained options).
      const assertion = await startAuthentication({ optionsJSON: JSON.parse(this.waOptionsJson) });

      // 3. Verify + create the session. The ceremony token is consumed
      //    server-side on SUCCESS only — on a failed assertion it stays
      //    valid, so it is RETAINED here for a retry (only cleared on
      //    success or destroy).
      const ceremonyToken = this.waChallengeToken;
      if (!ceremonyToken) {
        throw new Error('missing ceremony token');
      }
      await firstValueFrom(
        this.authService.passkeyAuthenticationVerify(ceremonyToken, JSON.stringify(assertion)),
      );
      // Success: the token is now consumed server-side — drop both retained
      // values and complete the login.
      this.waChallengeToken = null;
      this.waOptionsJson = null;
      this.completeLogin();
    } catch (error: unknown) {
      // User cancellation is NOT a failed proof attempt — return silently to
      // the method-choice state. The ceremony token + options remain
      // retained, so the next "Use passkey" click re-runs the prompt.
      if (isUserCancellation(error)) {
        this.logger.info('Passkey assertion cancelled by user');
        this.passkeyBusy.set(false);
        return;
      }
      this.logger.error('Passkey assertion failed', error);
      // Failed assertion: the ceremony token is still server-side-valid
      // (single-use on SUCCESS only) and stays retained for a retry. The
      // message is honest about both options: retry the prompt, or fall back
      // to TOTP (which re-runs the login handoff — see useTotpInstead()).
      this.passkeyError.set('Verification failed. Try your passkey again, or use your authenticator code instead.');
      this.passkeyBusy.set(false);
      return;
    }
  }

  /** Shared post-login navigation (same target resolution as onSubmit()). */
  private completeLogin(): void {
    this.passkeyBusy.set(false);
    const target = sessionStorage.getItem('starter-post-login-redirect') || '/dashboard';
    sessionStorage.removeItem('starter-post-login-redirect');
    void this.router.navigate([target]);
  }
}

/**
 * True when the error is the browser's user-cancellation signal (the platform
 * passkey prompt was dismissed). Mirrors security-settings.component.ts's
 * isUserCancellation() — kept local to avoid coupling the two screens.
 */
function isUserCancellation(error: unknown): boolean {
  const name = (error as { name?: string })?.name ?? '';
  return name === 'NotAllowedError' || name === 'AbortError';
}

/**
 * One-shot in-memory handoff for the MFA challenge token (task 5.1).
 *
 * The login/setup screen puts the challenge token here before navigating;
 * this component consumes it on init. The token NEVER touches web storage —
 * a module-level variable is process memory only and dies with the page,
 * which is exactly the lifetime a single-use challenge needs. A refresh
 * loses it by design (the parent re-logs in; the backend challenge TTL is
 * 5 minutes anyway).
 */
export class MfaChallengeBridge {
  private static pending: {
    challengeToken: string;
    purpose: 'verify' | 'enroll';
    hasPasskey: boolean;
  } | null = null;

  /**
   * Stage a challenge for the MFA-challenge screen.
   *
   * @param challengeToken string the step-1 challenge token (in-memory only)
   * @param purpose        'verify' | 'enroll' — which TOTP flow to render
   * @param hasPasskey     boolean OPTIONAL (Phase 2, task 2.5): true when the
   *                       login response carried hasPasskey — the screen then
   *                       defaults to the passkey option with a TOTP fallback.
   *                       Absent/false leaves the TOTP-only flow untouched.
   */
  static stage(challengeToken: string, purpose: 'verify' | 'enroll', hasPasskey = false): void {
    MfaChallengeBridge.pending = { challengeToken, purpose, hasPasskey };
  }

  /** Whether a challenge is currently staged, without consuming it. */
  static hasPending(): boolean {
    return MfaChallengeBridge.pending !== null;
  }

  static consumePendingChallenge(): {
    challengeToken: string;
    purpose: 'verify' | 'enroll';
    hasPasskey: boolean;
  } | null {
    const pending = MfaChallengeBridge.pending;
    MfaChallengeBridge.pending = null;
    return pending;
  }
}

/**
 * Reads the `exp` claim (epoch seconds) out of a challenge token for the
 * on-screen countdown ONLY. The payload is base64url-encoded JSON, not
 * encrypted (`ParentChallengeToken.cfc`'s `issue()`/`b64url()`) — this never
 * verifies the HMAC signature and grants nothing; the backend independently
 * re-checks both the signature and the expiry on every /mfa/verify call.
 * Returns null on any malformed input rather than throwing.
 */
function decodeChallengeExpiry(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[0];
    if (!payloadB64) {
      return null;
    }
    const padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (padded.length % 4)) % 4;
    const json = atob(padded + '='.repeat(padding));
    const payload: unknown = JSON.parse(json);
    if (
      typeof payload === 'object' &&
      payload !== null &&
      'exp' in payload &&
      typeof (payload as { exp: unknown }).exp === 'number'
    ) {
      return (payload as { exp: number }).exp;
    }
    return null;
  } catch {
    return null;
  }
}